/**
 * IAP wrapper. On iOS (Capacitor), invokes the native IAP plugin.
 * On web, returns a friendly notice.
 *
 * We keep this abstracted so the app can run identically on web and iOS.
 */
export const IAP_PRODUCT_ID = "com.mlc.taip.fullaccess";

export function isNativeIOS() {
  try {
    // Capacitor is injected in native shell
    // eslint-disable-next-line no-undef
    return !!(window.Capacitor && window.Capacitor.getPlatform && window.Capacitor.getPlatform() === "ios");
  } catch { return false; }
}

// Opaque runtime import — bypasses webpack static analysis so web build succeeds
// even when native-only Capacitor plugins aren't installed.
async function nativeImport(pkg) {
  try {
    // eslint-disable-next-line no-new-func
    const dynImport = new Function("p", "return import(p)");
    return await dynImport(pkg);
  } catch {
    return null;
  }
}

/**
 * Fetch localized price from Apple. Requires a Capacitor IAP plugin at runtime.
 * On web / when plugin unavailable, returns null so UI can show "Available on iOS".
 */
export async function fetchProductInfo() {
  if (!isNativeIOS()) return null;
  const mod = await nativeImport("@capacitor-community/in-app-purchases");
  if (!mod || !mod.InAppPurchases) return null;
  try {
    const { InAppPurchases } = mod;
    const res = await InAppPurchases.getProducts({ productIds: [IAP_PRODUCT_ID] });
    return (res.products || [])[0] || null;
  } catch {
    return null;
  }
}

/**
 * Trigger the native purchase sheet, then send the receipt to backend.
 */
export async function purchaseFullAccess(apiPost) {
  if (!isNativeIOS()) {
    throw new Error("Pembelian hanya tersedia dalam iOS App Store.");
  }
  const mod = await nativeImport("@capacitor-community/in-app-purchases");
  if (!mod || !mod.InAppPurchases) throw new Error("IAP plugin unavailable");
  const { InAppPurchases } = mod;
  const purchase = await InAppPurchases.purchase({ productId: IAP_PRODUCT_ID });
  const receipt = purchase.receipt || purchase.transactionReceipt;
  if (!receipt) throw new Error("Missing receipt");
  const res = await apiPost("/iap/verify", { receipt_data: receipt });
  return res;
}

export async function restorePurchases(apiPost) {
  if (!isNativeIOS()) {
    throw new Error("Restore hanya tersedia dalam iOS App Store.");
  }
  const mod = await nativeImport("@capacitor-community/in-app-purchases");
  if (!mod || !mod.InAppPurchases) throw new Error("IAP plugin unavailable");
  const { InAppPurchases } = mod;
  const res = await InAppPurchases.restorePurchases();
  const receipt = res?.receipt;
  if (!receipt) throw new Error("No prior purchase found");
  return await apiPost("/iap/verify", { receipt_data: receipt });
}

// Exposed so Login page can lazy-import Apple Sign In without webpack failure
export async function loadAppleSignIn() {
  return await nativeImport("@capacitor-community/apple-sign-in");
}
